import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';

import { DisplayInput } from '../DisplayInput';
import { formatCurrency, currency2Number, parseCurrency } from '../InputText';

import * as Styles from './index.styles';

const { StyledIcon } = Styles;

export const DisplayInputStepped = ({
    defaultValue,
    onChange,
    inputRef,
    currency,
    cents,
    thousands,
    stepValue,
    disabled,
    ...otherProps
}) => {
    const [value, setValue] = useState(
        formatCurrency(defaultValue, { currency, thousands, cents }),
    );
    const [disableDecrement, setDisableDecrement] = useState(false);

    const ref = inputRef || useRef();

    const handleIncrementClicked = () => {
        if (disabled) {
            return;
        }

        const newValue = currency2Number(value) + stepValue;
        if (disableDecrement) {
            setDisableDecrement(false);
        }
        const formattedValue = formatCurrency(newValue, { currency, thousands, cents });
        setValue(formattedValue);
        ref.current.value = formattedValue;
        onChange(formattedValue);
    };

    const handleDecrementClicked = () => {
        if (disabled || disableDecrement) {
            return;
        }

        let newValue = currency2Number(value) - stepValue;
        if (newValue <= 0) {
            newValue = 0;
            setDisableDecrement(true);
        }
        const formattedValue = formatCurrency(newValue, { currency, thousands, cents });
        setValue(formattedValue);
        ref.current.value = formattedValue;
        onChange(formattedValue);
    };

    const interceptOnChange = evt => {
        const newValue = String(evt.target.value || '').replace(/[^\d/]/g, '');
        let formattedValue;
        if (!Number.isNaN(newValue)) {
            formattedValue = Number(newValue) / 100;
        }
        formattedValue = formatCurrency(formattedValue, { currency, thousands, cents });
        setValue(formattedValue);
        ref.current.value = formattedValue;
        onChange(evt, { parsedValue: parseCurrency(formattedValue) });
    };

    return (
        <DisplayInput
            inputRef={ref}
            value={value}
            onChange={interceptOnChange}
            disabled={disabled}
            iconLeft={
                <StyledIcon
                    data-test-id="icon-minus-btn"
                    icon={['fas', 'minus']}
                    size="lg"
                    disabled={disabled || disableDecrement}
                    onClick={handleDecrementClicked}
                />
            }
            icon={
                <StyledIcon
                    data-test-id="icon-plus-btn"
                    icon={['fas', 'plus']}
                    size="lg"
                    disabled={disabled}
                    onClick={handleIncrementClicked}
                />
            }
            {...otherProps}
        />
    );
};

DisplayInputStepped.propTypes = {
    defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
    inputRef: PropTypes.shape({}),
    currency: PropTypes.string,
    thousands: PropTypes.string,
    cents: PropTypes.string,
    stepValue: PropTypes.number,
    disabled: PropTypes.bool,
};

DisplayInputStepped.defaultProps = {
    defaultValue: '',
    onChange: () => null,
    inputRef: null,
    currency: 'R$ ',
    thousands: '.',
    cents: ',',
    stepValue: 1,
    disabled: false,
};

DisplayInputStepped.Styles = Styles;
